import axios from 'axios'
import history from 'configureHistory'
import { push } from 'connected-react-router'
import {
	call,
	select,
	takeEvery,
	takeLatest,
	put,
	delay,
} from 'redux-saga/effects'

import { articleActions, articleTypes } from '.'

const getArticle = state => state.articles.article
const getSlug = state => (state.auth.team ? state.auth.team.slug : null)
const getUserId = state => state.auth.id

const editorHasChanged = state =>
	state.articles.article.contentHasChanged ||
	state.articles.article.titleHasChanged

const removeLoader = () => {
	document.getElementsByClassName('c-loading')[0].classList.add('is-finished')
}

function* getArticlesRequest({ isInitialRequest = false }) {
	try {
		const { data } = yield call(axios.get, 'articles/compact')

		yield put(articleActions.getArticlesSuccess(data))

		if (isInitialRequest) {
			yield call(removeLoader)
		}
	} catch (e) {
		//
	}
}

function* getArticlesIndexRequest() {
	try {
		const { data } = yield call(axios.get, 'articles')

		yield put(articleActions.getArticlesIndexSuccess(data))
	} catch (e) {
		//
	}
}

function* getArticleRequest({ id, update = false }) {
	try {
		// @TODO: Get clean data
		if (!id) return false
		if (!update) yield put(articleActions.updateArticleStatus(null, true))

		const { data } = yield call(axios.get, `articles/${id}`)

		// Use this to delay placeholder
		// yield delay(1000)
		yield put(articleActions.getArticleSuccess(data))

		// Do not track if it's only a refetch request
		if (update) return false

		if (
			history.location.state &&
			history.location.state.ref &&
			history.location.state.ref === 'search'
		) {
			const { keyword } = history.location.state

			yield call(axios.post, 'events', {
				type: 'ARTICLE_OPENED_FROM_SEARCH',
				article: data.id,
				data: JSON.stringify({
					keyword,
				}),
			})
		}

		yield delay(5000)
		yield call(axios.post, 'events', {
			type: 'ARTICLE_QUALIFIED_VIEW',
			article: data.id,
		})
	} catch (e) {
		// alert('Article not found, redirect to first article …')
		// yield call(history.push, '/')

		yield put(articleActions.updateArticleStatus(e.response.status))
	}
}

function* createArticleRequest({ article }) {
	try {
		const newArticle = {
			title: '',
			content: '',
			order: article.order,
			parent: article.parent || null,
		}

		const slug = yield select(getSlug)
		const { data } = yield call(axios.post, 'articles', newArticle)

		yield put(articleActions.createArticleSuccess(data))
		yield put(push(`/${slug}/${data.id}`))

		document.getElementById('title').focus()
	} catch (error) {
		//
	}
}

function* duplicateArticleRequest({ id }) {
	try {
		const { data } = yield call(axios.post, `articles/${id}`)
		const slug = yield select(getSlug)

		yield put(articleActions.duplicateArticleSuccess(data))
		yield put(push(`/${slug}/${data.id}`))
	} catch (error) {
		//
	}
}

function* updateArticleRequest() {
	try {
		const duration = 1000
		let a = null
		let startTime = null

		const response = yield select(getArticle)

		if (response.loading) return false

		startTime = Date.now()

		if (response.titleHasChanged && response.contentHasChanged) {
			a = {
				id: response.id,
				title: response.editorTitle,
				content: JSON.stringify(response.editorContent.toJSON()),
			}
		} else if (response.titleHasChanged) {
			a = {
				id: response.id,
				title: response.editorTitle,
			}
		} else if (response.contentHasChanged) {
			a = {
				id: response.id,
				content: JSON.stringify(response.editorContent.toJSON()),
			}
		}

		const articleKeys = ['title', 'order', 'parent', 'content']
		const articleData = Object.keys(a)
			.filter(key => articleKeys.includes(key))
			.reduce((obj, key) => {
				obj[key] = a[key]

				return obj
			}, {})

		if (Object.keys(articleData).length) {
			if (startTime && !(a.order || a.parent)) {
				const endTime = Date.now()

				if (endTime - startTime < duration) {
					yield delay(duration - (endTime - startTime))
				}
			}

			const { data } = yield call(
				axios.put,
				`articles/${a.id}`,
				articleData
			)

			yield put(articleActions.updateArticleSuccess(data))
		}
	} catch (error) {
		// yield put(articleActions.updateArticleFailure(error))
	}
}

function* updateArticlesRequest({ articles }) {
	try {
		const { data } = yield call(axios.put, `articles`, articles)

		yield put(articleActions.updateArticlesSuccess(data))
	} catch (error) {
		//
	}
}

function* rateArticleRequest({ id, state }) {
	try {
		const { data } = yield call(axios.post, `articles/${id}/rate`, {
			like: state,
		})
		const userId = yield select(getUserId)

		yield put(articleActions.rateArticleSuccess(userId, state))
	} catch (error) {
		//
	}
}

function* unlockArticleRequest({ id }) {
	try {
		yield call(axios.get, `articles/${id}/unlock`)
	} catch (error) {
		//
	}
}

function* removeArticleRequest({ id }) {
	try {
		const article = yield select(getArticle)

		yield call(axios.delete, `articles/${id}`)
		yield put(articleActions.removeArticleSuccess(id))

		if (article.id === id) {
			yield put(articleActions.updateArticleStatus(null))
			yield call(history.replace, '/')
		}
	} catch (error) {
		//
	}
}

function* changeArticleStateRequest({ id, state }) {
	try {
		const { data } = yield call(axios.put, `articles/${id}`, {
			draft: state,
		})

		yield put(articleActions.changeArticleStateSuccess(data.id, data.draft))
	} catch (error) {
		//
	}
}

function* changeOutdatedStateRequest({ id, state }) {
	try {
		const { data } = yield call(axios.put, `articles/${id}`, {
			outdated: state,
		})

		yield put(
			articleActions.changeOutdatedStateSuccess(data.id, data.outdated)
		)
	} catch (error) {
		//
	}
}

function* editorChanged() {
	try {
		const hasChanged = yield select(editorHasChanged)

		if (hasChanged) {
			const article = yield select(getArticle)

			if (article.lockedBy) {
				yield delay(500)
			}

			yield put(articleActions.updateArticleRequest())
		}
	} catch (error) {
		//
	}
}

function* wsArticleUpdate({ user, article }) {
	try {
		const currentArticle = yield select(getArticle)

		if (
			currentArticle.id === article.id &&
			article.lockedBy &&
			article.lockedBy.id !== user &&
			!article.title &&
			!article.content
		) {
			yield delay(1500)
			yield put(articleActions.getArticleRequest(article.id, true))
		}
	} catch (error) {
		//
	}
}

const articleSagas = [
	takeEvery(articleTypes.CREATE_ARTICLE_REQUEST, createArticleRequest),
	takeEvery(articleTypes.DUPLICATE_ARTICLE_REQUEST, duplicateArticleRequest),
	takeEvery(articleTypes.REMOVE_ARTICLE_REQUEST, removeArticleRequest),
	takeEvery(
		articleTypes.CHANGE_ARTICLE_STATE_REQUEST,
		changeArticleStateRequest
	),
	takeEvery(
		articleTypes.CHANGE_OUTDATED_STATE_REQUEST,
		changeOutdatedStateRequest
	),
	takeLatest(articleTypes.GET_ARTICLES_REQUEST, getArticlesRequest),
	takeLatest(
		articleTypes.GET_ARTICLES_INDEX_REQUEST,
		getArticlesIndexRequest
	),
	takeLatest(articleTypes.GET_ARTICLE_REQUEST, getArticleRequest),
	takeLatest(articleTypes.UPDATE_ARTICLE_REQUEST, updateArticleRequest),
	takeLatest(articleTypes.UPDATE_ARTICLES_REQUEST, updateArticlesRequest),
	takeLatest(articleTypes.UNLOCK_ARTICLE_REQUEST, unlockArticleRequest),
	takeLatest(articleTypes.RATE_ARTICLE_REQUEST, rateArticleRequest),
	takeLatest(articleTypes.CHANGE_EDITOR_TITLE, editorChanged),
	takeLatest(articleTypes.CHANGE_EDITOR_CONTENT, editorChanged),
	takeLatest(articleTypes.WS_ARTICLE_UPDATE, wsArticleUpdate),
]

export default articleSagas
